import type { RecordingStatus } from '@heidi/schema/ml-scribe';
import type { TabType } from '@heidi/session/contracts';

interface ProjectWorkspaceTab {
  name: TabType;
  documentId?: string;
}
interface ProjectWorkspacePendingLetter {
  temporaryId?: string;
}

import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import type { WorkspaceOpenArtifact, WorkspaceSnapshotV1 } from '../../../contracts';
import { deriveRecordingState } from '../../../contracts';
import type { Artifact } from '../artifacts/state';
import { openArtifactAtom } from '../artifacts/state';

/**
 * The session id a session-backed artifact resolves to, or ``undefined``
 * for non-session artifacts (patient / template-builder / created-objects).
 */
export function artifactSessionId(artifact: Artifact | null): string | undefined {
  if (!artifact) return undefined;
  switch (artifact.type) {
    case 'session':
    case 'note':
    case 'tasks':
    case 'codes':
    case 'letter':
    case 'document':
      return artifact.sessionId;
    default:
      return undefined;
  }
}

/**
 * Flatten the canvas ``Artifact`` discriminated union into the id-only
 * wire projection. ``activeTab`` is the live focused tab for a session
 * artifact (falls back to the artifact's requested ``focusTab``).
 */
export function toWorkspaceArtifact(
  artifact: Artifact | null,
  activeTab: ProjectWorkspaceTab | undefined,
  letterTab: TabType,
  pendingLetters: readonly ProjectWorkspacePendingLetter[] = []
): WorkspaceOpenArtifact | null {
  if (!artifact) return null;
  switch (artifact.type) {
    case 'session': {
      const focusedTab = activeTab?.name ?? artifact.focusTab;
      const focusedId = activeTab ? activeTab.documentId : artifact.focusDocumentId;
      const isPendingLetter =
        focusedTab === letterTab &&
        pendingLetters.some((entry) => entry.temporaryId === focusedId);
      return {
        type: 'session',
        session_id: artifact.sessionId,
        ...(focusedId &&
          !isPendingLetter &&
          (focusedTab === letterTab
            ? { letter_id: focusedId }
            : { document_id: focusedId })),
        ...(focusedTab && { active_tab: focusedTab }),
      };
    }
    case 'note':
      return {
        type: 'note',
        session_id: artifact.sessionId,
        ...(activeTab && { active_tab: activeTab.name }),
      };
    case 'tasks':
      return { type: 'tasks', session_id: artifact.sessionId };
    case 'codes':
      return { type: 'codes', session_id: artifact.sessionId };
    case 'document':
      return {
        type: 'document',
        session_id: artifact.sessionId,
        document_id: artifact.documentId,
      };
    case 'letter':
      return {
        type: 'letter',
        session_id: artifact.sessionId,
        letter_id: artifact.letterId,
      };
    case 'patient':
      return { type: 'patient', patient_id: artifact.patientId };
    case 'template-builder':
      return { type: 'template-builder', template_id: artifact.templateId };
    case 'form-template':
      return { type: 'form-template', template_id: artifact.templateId };
    case 'presentation':
      return { type: 'presentation', presentation_id: artifact.presentationId };
    case 'memory':
      return { type: 'memory', memory_id: artifact.memoryId };
    case 'workflow':
      return { type: 'workflow', workflow_id: artifact.workflowId };
    case 'created-objects':
      return { type: 'created-objects' };
    case 'session-list':
      // Id list / date range stay FE-local; wire only advertises the type so
      // the agent knows a list is open (ordinals resolved from prior tools).
      return { type: 'session-list' };
    case 'calendar':
    case 'mail':
    case 'files':
    case 'insurance':
    case 'library-document':
    case 'library':
    case 'document-export':
    case 'run-attachment':
      // Ephemeral local preview (presigned URL fetched at open). Not wire-projected.
      return null;
    case 'note-push-verification':
      // Ephemeral FE-local mapping-verification surface (candidate store, no
      // server record). Not wire-projected — there is no WorkspaceOpenArtifact
      // type for it and the agent needs nothing re-sent.
      return null;
    case 'evidence-references':
      // The sources the agent's own answer cited, read back out of an FE store.
      // Not wire-projected: re-sending them would tell the agent only what it
      // already said, and the run ids are meaningless to it.
      return null;
    default: {
      // Exhaustiveness guard: a new `Artifact` variant becomes a compile error
      // here rather than silently dropping out of the wire projection.
      const _exhaustive: never = artifact;
      void _exhaustive;
      return null;
    }
  }
}

/**
 * Derive the read-only workspace snapshot for the current Projects chat
 * canvas (see ``./types``). Sent on every turn so the agent knows what
 * the user is looking at and can reconcile a stale recording belief.
 *
 * Always returns a snapshot (never ``undefined``) — an ``idle`` /
 * ``open_artifact: null`` snapshot is itself a meaningful signal
 * ("nothing open, not recording"), which is exactly what corrects a
 * post-refresh desync.
 */
export interface ProjectWorkspaceSessionReader {
  (
    openSessionId: string | undefined,
    subjectSessionId: string | undefined
  ): {
    activeTab: ProjectWorkspaceTab | undefined;
    pendingLetters: readonly ProjectWorkspacePendingLetter[];
    recordingMeta:
      | {
          status: RecordingStatus | undefined;
          mode: WorkspaceSnapshotV1['recording']['mode'];
        }
      | undefined;
  };
}
export function useWorkspaceSnapshotCore(
  activeSessionId: string | null,
  useSessionState: ProjectWorkspaceSessionReader,
  letterTab: TabType
): WorkspaceSnapshotV1 {
  const artifact = useAtomValue(openArtifactAtom);

  const openSessionId = artifactSessionId(artifact);
  // The session we report recording for: the open session artifact wins,
  // else whatever is actively recording (so a banner-only recording with
  // no canvas still reconciles).
  const subjectSessionId = openSessionId ?? activeSessionId ?? undefined;

  const { activeTab, recordingMeta, pendingLetters } = useSessionState(
    openSessionId,
    subjectSessionId
  );

  return useMemo<WorkspaceSnapshotV1>(() => {
    const state = deriveRecordingState(
      activeSessionId,
      subjectSessionId,
      recordingMeta?.status
    );
    return {
      v: 1,
      open_artifact: toWorkspaceArtifact(artifact, activeTab, letterTab, pendingLetters),
      recording: {
        state,
        ...(subjectSessionId && { session_id: subjectSessionId }),
        ...(recordingMeta?.mode && { mode: recordingMeta.mode }),
      },
    };
  }, [
    artifact,
    activeTab,
    pendingLetters,
    letterTab,
    activeSessionId,
    subjectSessionId,
    recordingMeta?.status,
    recordingMeta?.mode,
  ]);
}
